import { updateOrganizationSettings } from 'actions';
import Icon from 'components/Icon';
import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useCurrentOrganization } from 'utils/util/hooks';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_ONLY } from 'views/Widgets/People/Filters/common';
import TemplateEditorModal from './TemplateEditorModal';
import TemplatesAdminHeader from './TemplatesAdminHeader';
import { TemplateTypeConfigs } from './templateTypeConfigs';
import {
  ContentTemplate,
  ContentTemplateMutationResponse,
  TEMPLATE_STATUSES,
  TEMPLATE_TYPE_NAME,
  TEMPLATE_TYPE_PLURAL_NAME,
  TemplateType,
} from './types';

interface TemplateActionsProps {
  template: ContentTemplate;
  onEdit: (template: ContentTemplate) => void;
  onToggleArchive: (template: ContentTemplate) => void;
}

const TemplateActions: FC<TemplateActionsProps> = ({
  template,
  onEdit,
  onToggleArchive,
}) => {
  const { formatMessage } = useIntl();
  const isArchived = template.status === TEMPLATE_STATUSES.ARCHIVED;
  const editLabel = formatMessage({
    id: 'app.views.administration.content_administration.templates.actions.edit',
    defaultMessage: 'Edit',
  });
  const archiveLabel = isArchived
    ? formatMessage({
        id: 'app.views.administration.content_administration.templates.actions.restore',
        defaultMessage: 'Restore',
      })
    : formatMessage({
        id: 'app.views.administration.content_administration.templates.actions.archive',
        defaultMessage: 'Archive',
      });

  return (
    <div className="text-nowrap">
      <Button
        color="light"
        size="sm"
        className="me-2"
        onClick={() => onEdit(template)}
        title={editLabel}
        aria-label={editLabel}
      >
        <Icon icon={ICONS.EDIT} />
      </Button>
      <Button
        color="light"
        size="sm"
        onClick={() => onToggleArchive(template)}
        title={archiveLabel}
        aria-label={archiveLabel}
      >
        <Icon icon={isArchived ? ICONS.REFRESH : ICONS.ARCHIVE} />
      </Button>
    </div>
  );
};

export interface TemplatesAdminPageProps {
  onCreateRef?: (handler: () => void) => void;
}

interface TemplatesAdminPageBaseProps extends TemplatesAdminPageProps {
  templateType: TemplateType;
  typeConfigs: TemplateTypeConfigs;
}

const TemplatesAdminPageBase: FC<TemplatesAdminPageBaseProps> = ({
  onCreateRef,
  templateType,
  typeConfigs,
}) => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const currentOrganization = useCurrentOrganization();

  const [templates, setTemplates] = useState<ContentTemplate[] | undefined>(
    undefined
  );
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ContentTemplate | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ContentTemplate | null>(
    null
  );

  const selectedType = templateType;

  const fetchTemplates = useCallback(() => {
    if (!currentOrganization?.id) return;
    setTemplates(undefined);
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'content-templates',
      { organization: currentOrganization.id, type: selectedType },
      (data, error, hardError) => {
        if (error || hardError) {
          setLoadError(error || hardError);
          return;
        }
        setLoadError(null);
        setTemplates(data?.results ?? []);
      }
    );
  }, [currentOrganization?.id, selectedType]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const applyMutation = useCallback(
    (saved: ContentTemplateMutationResponse) => {
      const { organization_settings, ...template } = saved;
      setTemplates((prev) => {
        const list = prev ?? [];
        const index = list.findIndex((t) => t.id === template.id);
        if (index === -1) {
          return [...list, template];
        }
        return list.map((t) => (t.id === template.id ? template : t));
      });
      if (organization_settings) {
        // creation flows (1:1s, development plans, promotion packets) read
        // templates from the organization settings held in the store
        dispatch(updateOrganizationSettings(organization_settings));
      }
    },
    [dispatch]
  );

  const handleCreate = useCallback(() => {
    setEditingTemplate(null);
    setIsEditorOpen(true);
  }, []);

  useEffect(() => {
    onCreateRef?.(handleCreate);
  }, [onCreateRef, handleCreate]);

  const handleEdit = useCallback((template: ContentTemplate) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  }, []);

  const toggleEditor = useCallback(() => {
    setIsEditorOpen((prev) => !prev);
  }, []);

  const handleSaved = useCallback(
    (saved: ContentTemplateMutationResponse) => {
      applyMutation(saved);
      toast.success(
        formatMessage({
          id: 'app.views.administration.content_administration.templates.toast.saved',
          defaultMessage: 'Template saved.',
        })
      );
    },
    [applyMutation, formatMessage]
  );

  const dismissArchive = useCallback(() => setArchiveTarget(null), []);

  const confirmToggleArchive = useCallback(() => {
    if (!archiveTarget || !currentOrganization?.id) return;
    const nextStatus =
      archiveTarget.status === TEMPLATE_STATUSES.ARCHIVED
        ? TEMPLATE_STATUSES.ACTIVE
        : TEMPLATE_STATUSES.ARCHIVED;
    ConfirmAPI.sendRequestToConfirm(
      'PATCH',
      `content-templates/${archiveTarget.id}`,
      { organization: currentOrganization.id, status: nextStatus },
      (data, error, hardError) => {
        setArchiveTarget(null);
        if (error || hardError || !data) {
          toast.error(
            getFriendlyErrorMessageIfErrorOccurred(error, hardError) ||
              formatMessage({
                id: 'app.views.administration.content_administration.templates.toast.archive_error',
                defaultMessage: 'Failed to update the template.',
              })
          );
          return;
        }
        applyMutation(data);
      }
    );
  }, [archiveTarget, currentOrganization?.id, applyMutation, formatMessage]);

  const rows = useMemo(() => {
    return (templates ?? [])
      .filter((t) => t.type === selectedType)
      .map((t) => ({ ...t, actions: t }));
  }, [templates, selectedType]);

  const describeContent = useCallback(
    (template: ContentTemplate): string =>
      typeConfigs[template.type]?.describeContent(template) ?? '',
    [typeConfigs]
  );

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        className: 'col-4',
        format: (_value, _uniqueId, _rowIndex, row: ContentTemplate) => (
          <div className="py-1">
            <div className="fw-bold">{row.name}</div>
            <div className="small text-muted">{describeContent(row)}</div>
          </div>
        ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.type',
          defaultMessage: 'Type',
        }),
        field: 'type',
        className: 'col-2',
        format: (value: TemplateType) =>
          TEMPLATE_TYPE_NAME(formatMessage, value),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.last_updated',
          defaultMessage: 'Last updated',
        }),
        field: 'updated_at',
        className: 'col-2 pe-0',
        format: (value: string | null) =>
          value ? (
            getPrettyDate({ dateString: value, locale })
          ) : (
            <span className="text-muted">{'—'}</span>
          ),
      },
      {
        name: (
          <span
            title={formatMessage({
              id: 'app.views.administration.content_administration.templates.table.updated_by',
              defaultMessage: 'Updated by',
            })}
          >
            <Icon icon={ICONS.PERSON} />
          </span>
        ),
        csvName: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.updated_by',
          defaultMessage: 'Updated by',
        }),
        field: 'updated_by',
        isPerson: true,
        format: FORMAT_AVATAR_ONLY,
        className: 'col-auto ps-0',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.status',
          defaultMessage: 'Status',
        }),
        field: 'status',
        className: 'col-1',
        format: (value: string) =>
          value === TEMPLATE_STATUSES.ARCHIVED ? (
            <span className="badge rounded-pill bg-secondary-soft text-dark px-3 py-2">
              <FormattedMessage
                id="app.views.administration.content_administration.templates.status.archived"
                defaultMessage="Archived"
              />
            </span>
          ) : (
            <span className="badge rounded-pill bg-success-soft text-dark px-3 py-2">
              <FormattedMessage
                id="app.views.administration.content_administration.templates.status.active"
                defaultMessage="Active"
              />
            </span>
          ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.table.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
        format: (value: ContentTemplate) => (
          <TemplateActions
            template={value}
            onEdit={handleEdit}
            onToggleArchive={setArchiveTarget}
          />
        ),
      },
    ],
    [formatMessage, locale, describeContent, handleEdit]
  );

  if (loadError) {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.administration.content_administration.templates.error.title',
          defaultMessage: 'Templates could not be loaded',
        })}
        subtitle={getFriendlyErrorMessageIfErrorOccurred(loadError, null)}
      />
    );
  }

  if (!templates) {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.content_administration.templates.loading',
          defaultMessage: 'Loading templates...',
        })}
      />
    );
  }

  const isArchiving = archiveTarget?.status !== TEMPLATE_STATUSES.ARCHIVED;

  return (
    <>
      <TemplatesAdminHeader selectedType={selectedType} />
      {rows.length === 0 ? (
        <EmptyState
          title={formatMessage(
            {
              id: 'app.views.administration.content_administration.templates.empty.title',
              defaultMessage: 'No {typeName} yet',
            },
            {
              typeName: TEMPLATE_TYPE_PLURAL_NAME(
                formatMessage,
                selectedType
              ).toLowerCase(),
            }
          )}
        >
          <Button color="primary" onClick={handleCreate}>
            <FormattedMessage
              id="app.views.administration.content_administration.templates.create"
              defaultMessage="Create template"
            />
          </Button>
        </EmptyState>
      ) : (
        <FilterablePeopleTable
          rows={rows}
          columns={columns}
          hideFilters={true}
          hideExportButton={true}
          hideHeader={true}
          showTable={true}
          showToggleFilters={false}
        />
      )}
      <TemplateEditorModal
        isOpen={isEditorOpen}
        toggle={toggleEditor}
        template={editingTemplate}
        defaultType={selectedType}
        typeConfigs={typeConfigs}
        onSaved={handleSaved}
      />
      <ConfirmationDialogModal
        isOpen={!!archiveTarget}
        toggle={dismissArchive}
        onClosed={dismissArchive}
        confirmCallback={confirmToggleArchive}
        confirmationButtonColor={isArchiving ? 'danger' : 'primary'}
        title={
          isArchiving
            ? formatMessage({
                id: 'app.views.administration.content_administration.templates.archive_modal.title',
                defaultMessage: 'Archive template',
              })
            : formatMessage({
                id: 'app.views.administration.content_administration.templates.restore_modal.title',
                defaultMessage: 'Restore template',
              })
        }
        description={
          isArchiving ? (
            <FormattedMessage
              id="app.views.administration.content_administration.templates.archive_modal.description"
              defaultMessage='"{name}" will no longer be offered when new content is created. Content that already uses it is not changed. You can restore it at any time.'
              values={{ name: archiveTarget?.name }}
            />
          ) : (
            <FormattedMessage
              id="app.views.administration.content_administration.templates.restore_modal.description"
              defaultMessage='"{name}" will be offered again when new content is created.'
              values={{ name: archiveTarget?.name }}
            />
          )
        }
        confirmText={
          isArchiving
            ? formatMessage({
                id: 'app.views.administration.content_administration.templates.archive_modal.confirm',
                defaultMessage: 'Archive',
              })
            : formatMessage({
                id: 'app.views.administration.content_administration.templates.restore_modal.confirm',
                defaultMessage: 'Restore',
              })
        }
      />
    </>
  );
};

export default React.memo(TemplatesAdminPageBase);
